/**
 * Onboarding-mall – Introduktion av ny medarbetare v10.0
 * Baserad på Tikabs onboardingdokument + LagbasN-specifika steg.
 *
 * role-värden:
 *   'chef'       → GL / AO-ansvarig / VD
 *   'it'         → IT-ansvarig (admin/superadmin)
 *   'mentor'     → Utsedd mentor (specifik uid)
 *   'ekonomi'    → Ekonomiansvarig
 *   'medarbetare'→ Den nyanställde själv
 *   'any'        → Vem som helst med behörighet
 */

export const PHASES = [
  { key: 'before',   label: 'Innan första dag' },
  { key: 'day1',     label: 'Dag 1' },
  { key: 'week1_2',  label: 'Vecka 1–2' },
  { key: 'followup', label: 'Uppföljning' },
];

export const SECTIONS = [
  // ─── FÖRBEREDELSER ───────────────────────────────────────────────
  {
    id: 'forberedelser',
    phase: 'before',
    title: 'Förberedelser',
    color: '#1565C0',
    bg: '#e3f2fd',
    items: [
      { id: 'equip_order',      text: 'Anmodan om inköp av utrustning och licenser till IT',                    role: 'chef' },
      { id: 'it_install',       text: 'Grundinstallation av dator inkl. Office 365 och e-post',                 role: 'it' },
      { id: 'sys_access',       text: 'Behörighet till ledningssystemet',                                        role: 'it' },
      { id: 'lagbasn_account',  text: 'Konto skapas i LagbasN (medarbetarregistret)',                           role: 'it',   lagbasn: '/employees' },
      { id: 'email_groups',     text: 'E-postadress tillagd i relevanta kontaktgrupper i Outlook',              role: 'it' },
      { id: 'inform_team',      text: 'Medarbetare informerade om ny kollega och position',                     role: 'chef' },
      { id: 'mentor_assigned',  text: 'Mentor utsedd',                                                          role: 'chef' },
      { id: 'flower',           text: 'Blomma planerad till första dagen',                                       role: 'chef' },
      { id: 'cal_booking',      text: 'Kalenderbokning dag 1 finns hos Adm, Ekonomi och vVD',                  role: 'chef' },
    ],
  },

  // ─── UTRUSTNING OCH ACCESS ───────────────────────────────────────
  {
    id: 'utrustning',
    phase: 'day1',
    title: 'Utrustning och access',
    color: '#6a1b9a',
    bg: '#f3e5f5',
    items: [
      { id: 'key_tag',          text: 'Nyckeltag kvitterat',                                                     role: 'chef' },
      { id: 'computer_phone',   text: 'Dator och telefon kvitterade',                                           role: 'chef' },
      { id: 'lock_alarm',       text: 'Genomgång av lås- och larmsystem',                                       role: 'chef' },
      { id: 'facility_tour',    text: 'Genomgång av lokalen – utrymningsvägar, brandskydd, hjärtstartare, gym, cykelrum, Convini', role: 'mentor' },
    ],
  },

  // ─── IT-KONTON OCH SYSTEM ────────────────────────────────────────
  {
    id: 'it_konton',
    phase: 'week1_2',
    title: 'IT-konton och system',
    color: '#00695c',
    bg: '#e0f2f1',
    items: [
      { id: 'email_sig_outlook', text: 'Mejlsignatur skapad i Outlook enligt standard',                        role: 'medarbetare' },
      { id: 'email_sig_lagbasn', text: 'Mailsignatur skapad i LagbasN',                                        role: 'medarbetare', lagbasn: '/marketing/signature' },
      { id: 'photo_office',      text: 'Profilbild uppladdad i Office / Teams',                                role: 'medarbetare' },
      { id: 'photo_lagbasn',     text: 'Profilbild uppladdad i LagbasN',                                       role: 'medarbetare', lagbasn: '/employees' },
      { id: 'calendar_shared',   text: 'Outlookkalender delad med organisationen (rubrik + plats synlig)',      role: 'medarbetare' },
      { id: 'office_templates',  text: 'Sökväg till Tikabs officemallar inlagd i Office',                      role: 'medarbetare' },
      { id: 'teams_background',  text: 'Tikabs mötesbakgrunder inlagda i Teams',                               role: 'medarbetare' },
      { id: 'onepassword',       text: 'Tillgång till 1Password',                                               role: 'it' },
      { id: 'timereport',        text: 'Genomgång av tidsskrivningssystem',                                     role: 'chef' },
      { id: 'id06',              text: 'ID06 beställd (vid behov)',                                             role: 'chef' },
    ],
  },

  // ─── EKONOMI OCH HR ──────────────────────────────────────────────
  {
    id: 'ekonomi_hr',
    phase: 'week1_2',
    title: 'Ekonomi och HR',
    color: '#E65100',
    bg: '#FFF3E0',
    items: [
      { id: 'payroll_info',  text: 'Löneuppgifter skickade till Ekonomi inför lönekörning',                    role: 'chef' },
      { id: 'hr_package',    text: 'HR-paketet (policys, sekretessavtal, befattningsbeskrivning, personalhandbok) skickat för digital signering', role: 'chef' },
      { id: 'expense_intro', text: 'Genomgång av frånvarorutiner, utlägg och reseräkning med Ekonomi',         role: 'ekonomi' },
    ],
  },

  // ─── INTRODUKTION OCH SOCIAL ─────────────────────────────────────
  {
    id: 'introduktion',
    phase: 'day1',
    title: 'Introduktion och social',
    color: '#2E7D32',
    bg: '#E8F5E9',
    items: [
      { id: 'mentor_meeting',   text: 'Mentor presenterad och 10-min intro inbokad under dag 1',               role: 'chef' },
      { id: 'lunch_day1',       text: 'Gemensam lunch dag 1 – Tikab bjuder, mentor medverkar',                 role: 'mentor' },
      { id: 'role_intro',       text: 'Genomgång av befattningen – system, aktiviteter och närmaste deadlines', role: 'chef' },
      { id: 'colleague_intros', text: 'Korta intervjuer med kollegor inom AO bokade och genomförda',           role: 'medarbetare' },
      { id: 'other_ao',         text: 'Möte med medarbetare från annat affärsområde bokat',                    role: 'medarbetare' },
      { id: 'meeting_series',   text: 'Interna mötesserier inlagda i kalendern (Tikabmöten, gruppmöten, AO-möten, trivsel)', role: 'mentor' },
    ],
  },

  // ─── LAGBASN – PROFIL OCH VERKTYG ────────────────────────────────
  {
    id: 'lagbasn',
    phase: 'week1_2',
    title: 'LagbasN – profil och verktyg',
    color: '#FF6B00',
    bg: '#fff8f0',
    items: [
      { id: 'lb_employee',  text: 'Uppgifter inlagda i medarbetarregistret i LagbasN',                         role: 'medarbetare', lagbasn: '/employees' },
      { id: 'lb_cv',        text: 'Konsultprofil / CV skapad och uppdaterad i LagbasN',                        role: 'medarbetare', lagbasn: '/profile' },
      { id: 'lb_iup',       text: 'Första IUP-samtal planerat i LagbasN',                                      role: 'chef',        lagbasn: '/hr/iup' },
      { id: 'lb_intro',     text: 'Genomgång av LagbasN – Dashboard, moduler och funktioner',                  role: 'mentor',      lagbasn: '/dashboard' },
    ],
  },

  // ─── LEDNINGSSYSTEM OCH KVALITET ─────────────────────────────────
  {
    id: 'ledningssystem',
    phase: 'week1_2',
    title: 'Ledningssystem och kvalitet',
    color: '#455A64',
    bg: '#ECEFF1',
    items: [
      { id: 'quality_walkthrough', text: 'Genomgång av kvalitetsledningssystemet "Vårt sätt att arbeta"',     role: 'chef' },
      { id: 'teams_app',           text: 'Teams-app för ledningssystemet hämtad',                              role: 'medarbetare' },
      { id: 'tiknet_intro',        text: 'Genomgång av Tiknet (intranet)',                                      role: 'mentor' },
      { id: 'fileserver',          text: 'Genomgång av filserver och katalogstruktur',                          role: 'mentor' },
      { id: 'room_booking',        text: 'Mötesrumsbokning – genomgång av system',                             role: 'medarbetare' },
      { id: 'phone_system',        text: 'Telefonisystem – genomgång',                                          role: 'medarbetare' },
    ],
  },

  // ─── UPPFÖLJNINGSSAMTAL ───────────────────────────────────────────
  {
    id: 'uppfoljning',
    phase: 'followup',
    title: 'Uppföljningssamtal',
    color: '#1565C0',
    bg: '#e3f2fd',
    items: [
      {
        id: 'followup_2w',
        text: 'Uppföljningssamtal efter 2 veckor – Hur upplever du den första tiden? Frågor? Planering framåt?',
        role: 'chef',
      },
      {
        id: 'followup_8w',
        text: 'Uppföljningssamtal efter 8 veckor – Hur upplever du den första tiden? Frågor? Planering framåt?',
        role: 'chef',
      },
      {
        id: 'followup_12w',
        text: 'Uppföljningssamtal efter 12 veckor – Hur upplever du den första tiden? Frågor? Planering framåt?',
        role: 'chef',
      },
    ],
  },
];

// Totalt antal aktiviteter (statisk mall – används som fallback)
export const TOTAL_ITEMS = SECTIONS.reduce((sum, s) => sum + s.items.length, 0);

// Hjälp: beräkna progress (0–100) från checks-objekt
// Accepterar valfritt sections-argument för dynamisk mall
export function calcProgress(checks = {}, sections = null) {
  const total = sections
    ? sections.reduce((sum, s) => sum + s.items.length, 0)
    : TOTAL_ITEMS;
  if (total === 0) return 0;
  const done = Object.values(checks).filter(c => c?.done).length;
  return Math.round((done / total) * 100);
}

// Ladda mall från Firestore (med fallback till statisk)
let _cachedTemplate = null;
export async function loadTemplate(db) {
  if (_cachedTemplate) return _cachedTemplate;
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const snap = await getDoc(doc(db, 'settings', 'onboardingTemplate'));
    if (snap.exists() && snap.data()?.sections?.length) {
      _cachedTemplate = snap.data().sections;
      return _cachedTemplate;
    }
  } catch {}
  _cachedTemplate = SECTIONS;
  return _cachedTemplate;
}

// Invalidera cache (anropas när mallen sparas)
export function invalidateTemplateCache() {
  _cachedTemplate = null;
}

// Etikett per roll
export const ROLE_LABELS = {
  chef:        'Chef / GL',
  it:          'IT',
  mentor:      'Mentor',
  ekonomi:     'Ekonomi',
  medarbetare: 'Medarbetaren',
  any:         'Valfri',
};

export const ROLE_COLORS = {
  chef:        { color: '#1565C0', bg: '#e3f2fd' },
  it:          { color: '#00695c', bg: '#e0f2f1' },
  mentor:      { color: '#6a1b9a', bg: '#f3e5f5' },
  ekonomi:     { color: '#E65100', bg: '#FFF3E0' },
  medarbetare: { color: '#FF6B00', bg: '#fff8f0' },
  any:         { color: '#455A64', bg: '#ECEFF1' },
};
